// src/pages/gene.codes/GeneticWheel/WheelDashboard.tsx
import React, { useEffect, useRef, useState } from "react";
import { Layout as AntLayout, Badge, Button, Dropdown, Input, Segmented, Tag, Alert, message } from "antd";
import { BarChartOutlined, ZoomInOutlined, SearchOutlined, DownOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../../../components/Layout";
import { InlineLoading, EmptyState } from "../../../components/shared";
import { useGeneticData } from "../../../hooks/genetic/useGeneticData";
import PanelList from "./dashboardPanels/PanelList";
import Sunburst from "./wheels/Sunburst";
import ZoomableSunburst from "./wheels/ZoomableSunburst";
import DetailPanel from "./dashboardPanels/DetailPanel";
import WheelInspector, { GeneDeleteTarget, NodeVisibilityTarget, selectedKind } from "./builder/WheelInspector";
import WheelNodeEditor, { NodeMutation } from "./builder/WheelNodeEditor";
import { deleteWheelGene, updateWheelGene } from "../../../api/geneCodes/WheelGeneApi";
import { updateTrait } from "../../../api/geneCodes/TraitApi";
import { updateStagingRecord } from "../../../api/geneCodes/StagingApi";
import { PanelLifecycleVerb, archivePanel, forkPanel, hidePanel, publishPanel, restorePanel, unhidePanel, unpublishPanel } from "../../../api/geneCodes/PanelApi";
import WheelSnpPicker, { PICKER_TASK } from "./builder/WheelSnpPicker";
import WheelResearchPane, { RESEARCH_TASK } from "./builder/WheelResearchPane";
import { useXoeFocusPublisher } from "../../../context/XoeFocusContext";
import { DETAIL_MIN_WIDTH, MIN_WHEEL, STAGE_GUTTER, detailWidthFor, paneWidthFor, wheelSizeFor } from "../../../utils/geneCodes/wheelLayout";
import { WheelSelection, depthOf, selectGene, selectSnp, selectTrait } from "../../../utils/geneCodes/wheelSelection";
import { resolveWheelGeneId } from "../../../utils/geneCodes/wheelGeneRing";
import { Trait } from "../../../types/geneCodes/TraitTypes";
import { Staging } from "../../../types/geneCodes/StagingTypes";
import { Panel } from "../../../types/geneCodes/Panel";
import { WheelNode } from "../../../types/geneCodes/geneticWheel";
import { WheelNodeKind, canAuthorWheels, childKindOf, isHiddenPanel, panelStatusOf } from "../../../utils/geneCodes/wheelAuthoring";
import "./wheelShell.css";
import "./builder/wheelBuilder.css";

const { Sider, Content } = AntLayout;

// C4: LAZY, deliberately (the ScreeningStep precedent) — a static import evaluates the Xoe
// message-thread chain, whose BodyModel reads `import.meta` at module load and breaks every
// existing Jest suite that mounts this shell; view mode never needs it at all.
const WheelXoePane = React.lazy(() => import("./builder/WheelXoePane"));

// The builder-mount key the Xoe pane answers to — the same seam RESEARCH_TASK uses. Owned
// HERE, not by the pane, because importing a constant from a lazy module defeats the split.
export const XOE_TASK = "xoe";

// The width a collapsed detail column still occupies — the same scale WheelModal collapses to.
const RAIL_COLLAPSED_WIDTH = 32;
const DETAIL_COLUMN = DETAIL_MIN_WIDTH;
const COMPACT_BREAKPOINT = 768;

const LIFECYCLE_CALLS: Record<PanelLifecycleVerb, (panelId: number) => Promise<Panel>> = {
	publish: publishPanel,
	unpublish: unpublishPanel,
	hide: hidePanel,
	unhide: unhidePanel,
	archive: archivePanel,
	restore: restorePanel,
	fork: forkPanel,
};

const LIFECYCLE_DONE: Record<PanelLifecycleVerb, string> = {
	publish: "Wheel published — it is live for patients now.",
	unpublish: "Wheel returned to draft — it is off patient screens until you publish it again.",
	hide: "Hidden — practitioners and patients no longer see this wheel",
	unhide: "Visible again to practitioners and assigned patients",
	archive: "Wheel archived — patients holding it no longer see it.",
	restore: "Wheel restored as a draft.",
	fork: "Published wheel forked — the builder is now on the new draft",
};

// A fork of a hidden wheel publishes as hidden, under the same server message, so only data.status tells.
const PUBLISHED_HIDDEN_DONE = "Wheel published — it stays hidden from practitioners and patients until you unhide it.";

// D13: stored reports are never rewritten, so a hide says so instead.
export const STORED_REPORTS_NOTICE = "Stored AI panel reports for this wheel were written before this change and may still mention it. Regenerate a sample's report (force) to remove it.";

// A refusal names WHY (e.g. the wheel is no longer published), so the server's message wins over ours.
const refusalMessage = (error: unknown): string | null => {
	const message = axios.isAxiosError(error) ? error.response?.data?.message : undefined;
	return typeof message === "string" && message ? message : null;
};

type WheelInk = "dark" | "light";

export type WheelDashboardMode = "view" | "build";

// Shared with App.tsx (route mount + XoeWidget suppression) — see wheelBuilderRoute.ts.
const VIEW_ROUTE = "/gene.codes/wheel";
const BUILD_ROUTE = "/gene.codes/wheel/build";

interface GeneticDashboardProps {
	mode?: WheelDashboardMode;
}

const MODE_OPTIONS = [
	{ label: "View", value: "view" },
	{ label: "Build", value: "build" },
];

const INK_OPTIONS = [
	{ label: "Dark ink", value: "dark" },
	{ label: "Light ink", value: "light" },
];

// `columnWidth` is what the right column ACTUALLY takes. It defaults to the detail minimum so the
// panel state keeps R11's two-step rule; the pane state passes its own width, because a half-viewport
// column the stage did not pay for would put the wheel behind it.
const centreWidthFor = (viewportWidth: number, detailInFlow: boolean, columnWidth: number = DETAIL_COLUMN) =>
	viewportWidth - (detailInFlow ? columnWidth : RAIL_COLLAPSED_WIDTH) - STAGE_GUTTER;

const GeneticDashboard: React.FC<GeneticDashboardProps> = ({ mode = "view" }) => {
	const [messageApi, contextHolder] = message.useMessage();
	const navigate = useNavigate();

	// The route guard is the boundary; this is the second half of it, so a build-mode render that
	// somehow bypassed the route still shows no edit affordance.
	const authoring = canAuthorWheels();
	const buildMode = mode === "build" && authoring;

	// Mount point for the node editor, S6's SNP picker and S10's research pane — they read this.
	const [builderTask, setBuilderTask] = useState<string>("");
	// R2: latched on the first Xoe open so the pane outlives a tab flip. Never true before that,
	// so a session that never opens Xoe still mounts none of the chat chain.
	const [xoePaneOpened, setXoePaneOpened] = useState(false);
	// Bumped on every successful node mutation so the wheel is remounted rather than diffed:
	// ZoomableSunburst re-initialises only when the panel id changes, so a same-panel edit would
	// otherwise repaint in one wheel and silently do nothing in the other.
	const [wheelRevision, setWheelRevision] = useState(0);

	const [sampleId, setSampleId] = useState<string>("");
	const [activeSampleId, setActiveSampleId] = useState<string>("");

	// Build mode stays on this hook rather than gaining React Query beside it: a second cache over the
	// same panels is two answers to "what is on this wheel", and `selectPanel` is already the one refetch.
	const { panels, selectedPanel, traits, snps, geneticData, loading, error, selectPanel, refreshPanels, clearSelectedPanel, aiPanelData } = useGeneticData({
		sampleId: activeSampleId,
		// A wheel is created as a draft, and the server lists published only unless asked; without
		// this the wheel you just made never appears in the toolbar selector.
		panelStatus: buildMode ? "all" : undefined,
		// Hidden nodes are drawn dimmed here so they can be unhidden; no viewer surface asks for them.
		includeHidden: buildMode,
	});

	const [selectedTrait, setSelectedTrait] = useState<Trait | null>(null);
	const [selectedGene, setSelectedGene] = useState<string | null>(null);
	const [selectedSnp, setSelectedSnp] = useState<Staging | null>(null);
	const [activeIndex, setActiveIndex] = useState<number>(0);

	const [visualizationMode, setVisualizationMode] = useState<string>("zoomable");
	const [wheelInk, setWheelInk] = useState<WheelInk>("dark");

	const [detailCollapsed, setDetailCollapsed] = useState(false);
	const [wheelPickerOpen, setWheelPickerOpen] = useState(false);

	const [windowSize, setWindowSize] = useState({
		width: window.innerWidth,
		height: window.innerHeight,
	});

	const detailInFlow = !detailCollapsed && windowSize.width >= COMPACT_BREAKPOINT;
	// The stage's HEIGHT only — it does not move when the detail column widens, so reading it back
	// cannot feed into the width it helped decide.
	const stageRef = useRef<HTMLDivElement>(null);
	const [stageHeight, setStageHeight] = useState(0);

	// R12: Research and Xoe are worked IN, so the column names its width and the wheel takes the
	// rest — the inverse of the panel state, and the same condition that swaps out the editor.
	// Reads `builderTask`, never the derived editor task below: an always-on editor must not widen the column.
	const paneOpen = buildMode && (builderTask === RESEARCH_TASK || builderTask === XOE_TASK);
	const paneWidth = Math.min(paneWidthFor(windowSize.width), windowSize.width - 48);
	const centreWidth = centreWidthFor(windowSize.width, detailInFlow, paneOpen ? paneWidth : DETAIL_COLUMN);
	// Before the first measurement the wheel is width-bound, which is what it was before it grew
	// height-aware — never a 320px dot on first paint.
	// The dashboard stage scrolls, so it can afford the floor the drawer's three columns cannot.
	const wheelSize = Math.max(MIN_WHEEL, wheelSizeFor(centreWidth, stageHeight || Number.POSITIVE_INFINITY));
	// Whatever the wheel could not use goes to the panel rather than staying as centre padding.
	const detailWidth = paneOpen ? paneWidth : Math.min(detailWidthFor(centreWidth, wheelSize, windowSize.width), windowSize.width - 48);

	useEffect(() => {
		setSelectedTrait(null);
		setSelectedGene(null);
		setSelectedSnp(null);
		setActiveIndex(0);
	}, [selectedPanel]);

	// Leaving the Research/Xoe pair is a deliberate close, so the session goes with it; the flip
	// between the two is not, and unmounting there destroys an in-flight turn nothing aborts.
	useEffect(() => {
		if (builderTask === XOE_TASK) {
			setXoePaneOpened(true);
			return;
		}
		if (builderTask !== RESEARCH_TASK) setXoePaneOpened(false);
	}, [builderTask]);

	// C4: publish WHICH wheel is being built onto the Xoe focus bus, so every turn sent
	// while the builder is open carries `context.wheel`. IDS ONLY — never a panel or trait
	// name. Outside the provider (most test suites) the publisher is a no-op.
	const { setWheelFocus, clearWheelFocus } = useXoeFocusPublisher();

	useEffect(() => {
		if (!buildMode || !selectedPanel) return undefined;
		const panelId = selectedPanel.id_panels;
		setWheelFocus({
			panelId,
			...(selectedTrait ? { traitId: selectedTrait.idtraits } : {}),
			// A4: the real wheel_genes id when the selected gene has a server row; null while it has
			// none (fallback ring). Omitted entirely while no gene is selected.
			...(selectedGene ? { wheelGeneId: resolveWheelGeneId(selectedTrait, snps, selectedGene) } : {}),
		});
		return () => clearWheelFocus(panelId);
	}, [buildMode, selectedPanel, selectedTrait, selectedGene, snps, setWheelFocus, clearWheelFocus]);

	useEffect(() => {
		const stage = stageRef.current;
		if (!stage || typeof ResizeObserver === "undefined") return undefined;
		const observer = new ResizeObserver(() => setStageHeight((prev) => (prev === stage.clientHeight ? prev : stage.clientHeight)));
		observer.observe(stage);
		setStageHeight(stage.clientHeight);
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		const handleResize = () => {
			setWindowSize((prev) => (prev.width === window.innerWidth && prev.height === window.innerHeight ? prev : { width: window.innerWidth, height: window.innerHeight }));
		};

		window.addEventListener("resize", handleResize);
		handleResize();
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	useEffect(() => {
		if (error.panels) {
			messageApi.error({ content: `Error loading panels: ${error.panels}`, duration: 3 });
		}
		if (error.selectedPanel) {
			messageApi.error({ content: `Error loading panel: ${error.selectedPanel}`, duration: 3 });
		}
		if (error.traits) {
			messageApi.error({ content: `Error loading traits: ${error.traits}`, duration: 3 });
		}
		if (error.snps) {
			messageApi.error({ content: `Error loading SNPs: ${error.snps}`, duration: 3 });
		}
		if (error.geneticData) {
			messageApi.error({ content: `Error loading genetic data: ${error.geneticData}`, duration: 3 });
		}
	}, [error, messageApi]);

	const revealDetail = () => setDetailCollapsed(false);

	// The tabs are a breadcrumb, so every click resolves the WHOLE path, not just its own level.
	const applySelection = (selection: WheelSelection) => {
		setSelectedTrait(selection.trait);
		setSelectedGene(selection.gene);
		setSelectedSnp(selection.snp);
		setActiveIndex(depthOf(selection));
		revealDetail();
	};

	const handleTraitClick = (trait: Trait) => applySelection(selectTrait(trait));

	const handleGeneClick = (gene: string) => applySelection(selectGene(gene, snps, traits, selectedTrait));

	const handleSnpClick = (snp: Staging) => applySelection(selectSnp(snp, traits));

	const onPanelClick = (panel: Panel) => {
		setSelectedTrait(null);
		setSelectedGene(null);
		setSelectedSnp(null);
		setActiveIndex(0);
	};

	// Zooming out is a selection too, so it goes through the same resolvers a click does — the wheel
	// says where it landed and the sidebar follows, rather than staying on the layer below it.
	const handleNavigateUp = (node: WheelNode) => {
		if (node.type === "trait") {
			applySelection(selectTrait(node.data as Trait));
			return;
		}
		if (node.type === "gene") {
			applySelection(selectGene(node.name, snps, traits, selectedTrait));
			return;
		}
		onPanelClick(node.data as Panel);
	};

	// The editor is a section of this column, not a modal, so SELECTING a node is the edit intent — the
	// same `builderTask` string an add sets, just derived. An explicit task always wins, which is what
	// keeps the editor out of the column while the picker or a Research/Xoe pane holds the seam.
	const editorTask = builderTask || (selectedPanel ? `edit:${selectedKind(selectedTrait, selectedGene, selectedSnp)}` : "");

	const toggleDetail = () => setDetailCollapsed((prev) => !prev);

	// The picker floats over the stage, so a selection made in it has to hand the stage back.
	const handleSelectPanel = (panelId: number, sample: string | undefined) => {
		setWheelPickerOpen(false);
		selectPanel(panelId, sample);
	};

	// PanelList carries trait names, not traits — the wheel is the one that holds the records.
	const handleSelectTraitByName = (traitName: string) => {
		const trait = traits.find((entry) => entry.trait_name === traitName);
		if (trait) handleTraitClick(trait);
	};

	// Each of these is a seam, not a feature: the node editor, the picker (S6) and the research pane
	// (S10) mount off `builderTask`.
	const handleCreateWheel = () => {
		setWheelPickerOpen(false);
		setBuilderTask("create:panel");
	};

	const handleAddNode = (kind: WheelNodeKind) => setBuilderTask(`add:${kind}`);

	const handlePickSnps = () => setBuilderTask(PICKER_TASK);

	// Emptying the seam is the whole mechanism: editorTask then falls back to the derived edit:*.
	const handleOpenEdit = () => setBuilderTask("");

	const handleOpenResearch = () => setBuilderTask(RESEARCH_TASK);

	const handleOpenXoe = () => setBuilderTask(XOE_TASK);

	// The + wedge adds the node kind the RING BELOW holds — childKindOf is that rule, in one place.
	const handleAddFromWheel = (parent: WheelNode) => {
		const kind = childKindOf(parent.type as WheelNodeKind);
		if (kind) setBuilderTask(`add:${kind}`);
	};

	// The picker stays open on its result, so it refreshes without closing — same seam, no second mechanism.
	const refreshSelectedPanel = () => {
		setWheelRevision((prev) => prev + 1);
		if (selectedPanel) selectPanel(selectedPanel.id_panels, activeSampleId || undefined);
	};

	// C5: a confirmed/undone Xoe wheel batch lands here — the SAME selectPanel/wheelRevision seam.
	// A batch that applied to a different wheel (a fork of a published target, or create_wheel)
	// switches the builder to it; the fork is surfaced, never silent.
	const handleWheelActionsApplied = ({ panelId: appliedPanelId, forkedFromPanelId }: { panelId: number | null; forkedFromPanelId: number | null }) => {
		setWheelRevision((prev) => prev + 1);
		if (forkedFromPanelId != null) {
			messageApi.info({ content: "Published wheel forked — changes applied to the new draft", duration: 4 });
		}
		if (appliedPanelId != null && appliedPanelId !== selectedPanel?.id_panels) {
			void refreshPanels();
			selectPanel(appliedPanelId, activeSampleId || undefined);
			return;
		}
		if (selectedPanel) selectPanel(selectedPanel.id_panels, activeSampleId || undefined);
	};

	// A5: gene delete — the inspector's Popconfirm already showed the blast radius; the toast then
	// reports what the SERVER detached, and the refetch runs through the same seam as every mutation.
	const handleDeleteGene = async ({ wheelGeneId, name }: GeneDeleteTarget) => {
		try {
			const result = await deleteWheelGene(wheelGeneId);
			messageApi.success({ content: `${name} deleted — ${result.deleted_snp_count} ${result.deleted_snp_count === 1 ? "SNP" : "SNPs"} detached from this trait.`, duration: 4 });
			setSelectedGene(null);
			setSelectedSnp(null);
			setActiveIndex(selectedTrait ? 1 : 0);
			setWheelRevision((prev) => prev + 1);
			if (selectedPanel) selectPanel(selectedPanel.id_panels, activeSampleId || undefined);
		} catch {
			messageApi.error({ content: `Could not delete ${name}.`, duration: 4 });
		}
	};

	// R5: the wheel's own lifecycle. Every transition ends through the same seam as handleNodeSaved,
	// and follows the id the SERVER returned — a fork answers with the new draft, not the origin.
	const handleLifecycle = async (action: PanelLifecycleVerb) => {
		if (!selectedPanel) return;

		try {
			const updated = await LIFECYCLE_CALLS[action](selectedPanel.id_panels);
			setWheelRevision((prev) => prev + 1);
			await refreshPanels();
			selectPanel(updated.id_panels, activeSampleId || undefined);

			if (action === "fork") {
				messageApi.info({ content: LIFECYCLE_DONE.fork, duration: 4 });
				return;
			}

			messageApi.success({ content: action === "publish" && isHiddenPanel(updated) ? PUBLISHED_HIDDEN_DONE : LIFECYCLE_DONE[action], duration: 4 });
		} catch (error) {
			messageApi.error({ content: refusalMessage(error) ?? `Could not ${action} this wheel.`, duration: 5 });
		}
	};

	// The one invalidation seam: `selectPanel` is already the single refetch path, so a mutation ends
	// by re-running it for the panel the wheel is showing.
	const handleNodeSaved = async (mutation: NodeMutation) => {
		setBuilderTask("");
		setWheelRevision((prev) => prev + 1);

		if (mutation.kind === "panel") {
			await refreshPanels();
			if (mutation.action === "delete") {
				clearSelectedPanel();
				return;
			}
			if (mutation.panelId) selectPanel(mutation.panelId, activeSampleId || undefined);
			return;
		}

		if (selectedPanel) selectPanel(selectedPanel.id_panels, activeSampleId || undefined);
	};

	// The inspector's visibility switch. Resolves false on a refusal so the switch can revert.
	const handleNodeVisibility = async (target: NodeVisibilityTarget, hidden: boolean): Promise<boolean> => {
		try {
			if (target.kind === "trait") await updateTrait(target.trait.idtraits, { ...target.trait, is_hidden: hidden });
			else if (target.kind === "gene") await updateWheelGene(target.wheelGeneId, { is_hidden: hidden });
			else await updateStagingRecord(target.snp.idstaging, { ...target.snp, is_hidden: hidden });
		} catch (error) {
			messageApi.error({ content: refusalMessage(error) ?? `Could not ${hidden ? "hide" : "show"} this ${target.kind === "snp" ? "SNP" : target.kind}.`, duration: 5 });
			return false;
		}

		if (hidden) messageApi.info({ content: STORED_REPORTS_NOTICE, duration: 8 });
		await handleNodeSaved({ kind: target.kind, action: "update" });
		return true;
	};

	const handleLoadSample = () => {
		if (sampleId.trim()) {
			setActiveSampleId(sampleId.trim());
		} else {
			setActiveSampleId("");
		}
	};

	const renderVisualization = () => {
		if (loading.selectedPanel || loading.traits || loading.snps) {
			return <InlineLoading message="Loading genetic data…" />;
		}

		if (!selectedPanel) {
			return (
				<EmptyState
					title="Select a panel to view the genetic wheel"
					description="Choose a wheel from the toolbar to visualise genetic data"
					action={{ label: "Show wheels", onClick: () => setWheelPickerOpen(true) }}
				/>
			);
		}

		// R13: both wheels draw the add wedge now, so the view is the author's choice in build mode too.
		const Wheel = visualizationMode === "multi" ? Sunburst : ZoomableSunburst;

		return (
			<Wheel
				key={`${selectedPanel.id_panels}:${wheelRevision}`}
				selectedPanel={selectedPanel}
				traits={traits}
				snps={snps}
				geneticResults={geneticData}
				onTraitClick={handleTraitClick}
				onGeneClick={handleGeneClick}
				onSnpClick={handleSnpClick}
				width={wheelSize}
				height={wheelSize}
				ink={wheelInk}
				onAddNode={buildMode ? handleAddFromWheel : undefined}
				onNavigateUp={handleNavigateUp}
			/>
		);
	};

	return (
		<Layout>
			{contextHolder}
			<div className="wheel-dash">
				<div className="wheel-dash__toolbar">
					<div className="wheel-dash__toolbar-row">
						<div className="wheel-dash__toolbar-group">
							<div className="wheel-dash__field">
								<span className="wheel-dash__field-label">Wheel:</span>
								<Dropdown
									open={wheelPickerOpen}
									onOpenChange={setWheelPickerOpen}
									trigger={["click"]}
									placement="bottomLeft"
									dropdownRender={() => (
										<div className="wheel-dash__wheel-popup" data-testid="wheel-selector-popup">
											<PanelList
												panels={panels}
												selectedPanelId={selectedPanel?.id_panels || null}
												onSelectPanel={handleSelectPanel}
												loading={loading.panels}
												sampleId={activeSampleId}
												selectedTraitName={selectedTrait?.trait_name ?? null}
												onSelectTrait={handleSelectTraitByName}
												onCreateWheel={buildMode ? handleCreateWheel : undefined}
												showStatus={buildMode}
											/>
										</div>
									)}
								>
									<Button size="small" className="wheel-dash__wheel-trigger" aria-expanded={wheelPickerOpen} aria-label="Select wheel" data-testid="wheel-selector">
										<span className="wheel-dash__wheel-name" data-testid="wheel-selector-name">{selectedPanel ? selectedPanel.name_panel : "Select a wheel"}</span>
										{selectedPanel ? (
											<Tag className={`wheel-dash__wheel-status mg-panel-row__status--${panelStatusOf(selectedPanel)}`} icon={isHiddenPanel(selectedPanel) ? <EyeInvisibleOutlined /> : undefined} data-testid="wheel-selector-status">
												{panelStatusOf(selectedPanel)}
											</Tag>
										) : null}
										<DownOutlined />
									</Button>
								</Dropdown>
							</div>

							<div className="wheel-dash__field">
								<label htmlFor="sampleId" className="wheel-dash__field-label">
									Sample ID:
								</label>
								<Input
									id="sampleId"
									value={sampleId}
									onChange={(e) => setSampleId(e.target.value)}
									placeholder="Enter sample ID (optional)"
									className="wheel-dash__sample-input"
									size="small"
									onPressEnter={handleLoadSample}
								/>
								<Button type="primary" onClick={handleLoadSample} loading={loading.geneticData} size="small" icon={<SearchOutlined />}>
									{loading.geneticData ? "Loading…" : "Load sample"}
								</Button>
							</div>

							{activeSampleId && (
								<div className="wheel-dash__badges">
									<Badge count={`Active: ${activeSampleId}`} className="wheel-dash__badge-active" />
									<Badge count={`${geneticData.length} genetic results`} className={`wheel-dash__badge-count${geneticData.length > 0 ? " wheel-dash__badge-count--loaded" : ""}`} />
								</div>
							)}
						</div>

						<div className="wheel-dash__toolbar-group">
							<Segmented
								value={visualizationMode}
								onChange={setVisualizationMode}
								options={[
									{ label: "Multi-layer", value: "multi", icon: <BarChartOutlined /> },
									{ label: "Zoomable", value: "zoomable", icon: <ZoomInOutlined /> },
								]}
								size="small"
								aria-label="Wheel view"
							/>
							<Segmented size="small" value={wheelInk} onChange={(value) => setWheelInk(value as WheelInk)} options={INK_OPTIONS} aria-label="Wheel ink" />
							{authoring && (
								<Segmented
									size="small"
									value={mode}
									onChange={(value) => navigate(value === "build" ? BUILD_ROUTE : VIEW_ROUTE)}
									options={MODE_OPTIONS}
									aria-label="Wheel mode"
									data-testid="wheel-mode-switch"
								/>
							)}
						</div>
					</div>

					{activeSampleId && geneticData.length === 0 && !loading.geneticData && !error.geneticData && (
						<Alert message="No genetic data found for this sample. All SNPs will be displayed without genetic analysis." type="warning" showIcon className="wheel-dash__notice" />
					)}
				</div>

				<AntLayout className="wheel-dash__body">
					<Content className={`wheel-dash__content${buildMode ? " wheel-dash__content--build" : ""}`}>
						<div className="wheel-dash__stage" ref={stageRef}>{renderVisualization()}</div>
						{buildMode && selectedPanel && (
							<div className="mg-wheel-build-hint">Click a segment to select it · click the dashed wedge to add a node</div>
						)}
					</Content>

					<Sider
						collapsed={detailCollapsed}
						collapsedWidth={RAIL_COLLAPSED_WIDTH}
						width={detailWidth}
						className={`wheel-dash__detail${!detailCollapsed && windowSize.width < COMPACT_BREAKPOINT ? " wheel-dash__detail--overlay" : ""}`}
					>
						<Button
							type="text"
							icon={detailCollapsed ? "‹" : "›"}
							onClick={toggleDetail}
							aria-label={detailCollapsed ? "Show panel detail" : "Hide panel detail"}
							className={`wheel-dash__toggle wheel-dash__toggle--right${detailCollapsed ? " wheel-dash__toggle--right-collapsed" : ""}`}
						/>

						{!detailCollapsed && (
							<div className="wheel-dash__pane">
								{buildMode && (
									<WheelInspector
										panel={selectedPanel}
										trait={selectedTrait}
										gene={selectedGene}
										snp={selectedSnp}
										snps={snps}
										onAddNode={handleAddNode}
										onPickSnps={handlePickSnps}
										onOpenEdit={handleOpenEdit}
										onOpenResearch={handleOpenResearch}
										onOpenXoe={handleOpenXoe}
										onDeleteGene={handleDeleteGene}
										onLifecycle={handleLifecycle}
										onSetNodeHidden={handleNodeVisibility}
									/>
								)}
								{buildMode && (
									<div className="mg-wheel-builder-mount" data-testid="wheel-builder-mount" data-task={builderTask}>
										<WheelNodeEditor
											task={editorTask}
											panel={selectedPanel}
											trait={selectedTrait}
											gene={selectedGene}
											snp={selectedSnp}
											snps={snps}
											onClose={() => setBuilderTask("")}
											onSaved={handleNodeSaved}
										/>
										<WheelSnpPicker
											task={builderTask}
											panel={selectedPanel}
											trait={selectedTrait}
											gene={selectedGene}
											panelSnps={snps}
											onClose={() => setBuilderTask("")}
											onAttached={refreshSelectedPanel}
										/>
										{/* C4: one detail column, two tabbed panes. The strip drives the SAME
										    builderTask seam the panes mount off — no second mechanism. */}
										<WheelResearchPane
											task={builderTask}
											panel={selectedPanel}
											trait={selectedTrait}
											onClose={() => setBuilderTask("")}
											onAttached={refreshSelectedPanel}
										/>
										{/* R2: mounted from the first Xoe open until the pair is left, and merely
										    HIDDEN behind the Research tab — its turn state has no store behind it. */}
										{xoePaneOpened && selectedPanel && (
											<React.Suspense fallback={<InlineLoading message="Loading Xoe…" minHeight={120} />}>
												<WheelXoePane
													hidden={builderTask !== XOE_TASK}
													onClose={() => setBuilderTask("")}
													panelId={selectedPanel.id_panels}
													onWheelActionsApplied={handleWheelActionsApplied}
												/>
											</React.Suspense>
										)}
									</div>
								)}
								{/* R14: the editor IS the builder's panel, so the read-only display is a VIEW-mode surface now. */}
								{!buildMode && (
									<div className="wheel-dash__detail-slot">
										<DetailPanel
											selectedPanel={selectedPanel}
											selectedTrait={selectedTrait}
											selectedGene={selectedGene}
											selectedSnp={selectedSnp}
											snps={snps}
											loading={loading.selectedPanel}
											activeIndex={activeIndex}
											setActiveIndex={setActiveIndex}
											aiPanelData={aiPanelData}
											geneticData={geneticData}
										/>
									</div>
								)}
							</div>
						)}
					</Sider>
				</AntLayout>
			</div>
		</Layout>
	);
};

export default GeneticDashboard;
